import React, { type ButtonHTMLAttributes, type DetailedHTMLProps } from 'react';
import useOneOnOneStore from '@/lib/zustand/store/oneOnOneStore';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import Day from '../utils/day';

type DayProps = {
  date: Day;
  availabilityScore: number;
  hasAvailability: boolean;
} & DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

export default function DayButton({
  date,
  availabilityScore,
  hasAvailability,
  ...props
}: DayProps): React.JSX.Element {
  const { start, end, selectedDate, setSelectedDate } = useOneOnOneStore();

  const now = Day.todayWithOffset(0);

  // Facts about the current date used to apply formatting/logic later.

  const isToday = date.toString() === now.toString();

  const isSelected = selectedDate ? date.toString() === selectedDate.toString() : false;

  const isDisabled = !hasAvailability;

  return (
    <button
      type='button'
      className={twMerge(
        clsx(
          'p-1 transition-all flex flex-col items-center outline-accent-600 relative !text-xs',
          props.className,
          {
            'font-semibold bg-slate-800  text-slate-200 border-brand-primary-border hocus:!border-red-500 hocus:shadow-sm hocus:shadow-accent-100 hocus:-mt-0.5 hocus:z-10 hocus:mb-0.5 border':
              !isDisabled,
            'bg-slate-700 text-gray-500 border border-brand-primary-border cursor-not-allowed':
              isDisabled,
            'bg-brand-primary !text-black': isSelected && !isToday,
            'bg-accent-600 hover:bg-accent-500': isSelected && isToday,
            'text-white': isSelected,
          }
        )
      )}
      disabled={isDisabled}
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      aria-label={`${isToday ? 'Today' : ''} ${
        isDisabled ? 'Unavailable' : 'Available'
      } date ${date.toString()} in calendar`}
      onClick={() => {
        setSelectedDate(date);
      }}
      {...props}
    >
      <div className='flex flex-col items-center justify-between leading-none'>
        <p
          className={clsx('font-semibold text-[0.55rem] leading-0 h-3 items-center flex', {
            'text-white': isSelected,
            'text-brand-primary': !isSelected,
          })}
        >
          {isToday && 'TODAY'}
        </p>
        <time className='leading-0 items-center flex flex-col gap-0'>
          <span className='font-semibold !text-sm p-0'>{Day.formatDate(date).day}</span>
          <span className='text-xs p-0'>{Day.formatDate(date).month}</span>
        </time>
        <figure className='flex items-center space-x-0.5 h-2 justify-center' aria-hidden='true'>
          {Array.from({ length: isDisabled ? 0 : availabilityScore }).map((_, index) => (
            <div
              key={`availability-bar-${index}`}
              className={clsx('rounded-full w-1 h-1', {
                'bg-white': isSelected,
                'bg-green-600': !isSelected,
              })}
            />
          ))}
        </figure>
      </div>
    </button>
  );
}
